import { type FanShopProductResponseLite, type FanShopProductVariant, isWeb, useIntl } from '@capsicum/common';
import { useCallback, useMemo } from 'react';

import { useProductQueries } from '../data-access/queries/useProductQueries';
import { getPriceForVariant } from '../util';

type UseProductProps = {
	variantId?: string;
};

export const useProduct = ({ variantId }: UseProductProps = {}) => {
	const { data: variant, isFetching, isLoading, refetch: refetchProduct } = useProductQueries(variantId);
	const { getLocalizedField } = useIntl();

	const getTokenRequired = useCallback(
		(v: FanShopProductVariant = variant || {}) => v?.product?.productTierLevel?.minTokenRequired || 0,
		[variant]
	);

	const getSsuAmount = useCallback(
		(v: FanShopProductVariant = variant || {}) => getPriceForVariant(v?.sellingPrice, v?.product?.sellingPrice),
		[variant]
	);

	const getRedeemOptions = useCallback(
		(v: FanShopProductVariant = variant || {}) => v.product?.redeemOptions || {},
		[variant]
	);

	const getImages = useCallback((objWithImages?: FanShopProductResponseLite | FanShopProductVariant) => {
		return objWithImages?.images?.filter(image => {
			return isWeb() ? image?.imageType?.type?.includes('desktop') : !image?.imageType?.type?.includes('desktop');
		});
	}, []);

	const allImages = useMemo(() => {
		if (getImages(variant)?.length) {
			return getImages(variant) || [];
		}

		return getImages(variant?.product) || [];
	}, [getImages, variant]);

	// TODO: get images differently (see partners)
	const getMainImage = useCallback(
		(v: FanShopProductVariant = variant || {}) => {
			const checkImages = (objWithImages?: FanShopProductResponseLite | FanShopProductVariant) => {
				return objWithImages?.images
					?.filter(image =>
						isWeb()
							? image?.imageType?.type?.includes('desktop')
							: !image?.imageType?.type?.includes('desktop')
					)
					?.find(image => image?.imageType?.type?.includes('_image_'));
			};

			return checkImages(v) || checkImages(v?.product);
		},
		[variant]
	);

	const getCategory = useCallback(
		(v: FanShopProductVariant = variant || {}) => {
			const productCategories = v?.product?.categories;

			if (!productCategories) return null;

			// TODO: handle BE translations here. Blocked by
			return productCategories.map(c => getLocalizedField('name', c)).join(' / ');
		},
		[getLocalizedField, variant]
	);

	return {
		allImages,
		getCategory,
		getMainImage,
		getRedeemOptions,
		getSsuAmount,
		getTokenRequired,
		isFetching,
		isLoading,
		refetchProduct,
		variant
	};
};
